import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { closeTopLayer, decideBack, layerCount } from '@/lib/backStack';
import { platformName } from '@/lib/platform';
import { useToast } from '@/store/toast';

/**
 * Запасной поиск открытого меню: несколько старых меню (раздел «Звонки»,
 * «Календарь», выбор воронки канала) рисуют своё затемнение `.scrim` сами,
 * без регистрации в стеке. Нажатие на затемнение их закрывает — его и жмём.
 */
function closeUnregisteredMenu(): boolean {
  const scrims = document.querySelectorAll<HTMLElement>('.scrim');
  const top = scrims[scrims.length - 1];
  if (!top) return false;
  top.click();
  return true;
}

/** Кнопка «назад» Андроида (см. lib/backStack.ts). На iOS и в браузере ничего не делает. */
export function BackButtonHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const loc = useRef(location);
  loc.current = location;
  const lastPrompt = useRef(0);

  useEffect(() => {
    if (platformName() !== 'android') return;
    let remove: (() => void) | null = null;
    let cancelled = false;
    void import('@capacitor/app').then(async ({ App }) => {
      const handle = await App.addListener('backButton', () => {
        const hasRegistered = layerCount() > 0;
        const hasScrim = !hasRegistered && !!document.querySelector('.scrim');
        const action = decideBack({
          hasLayer: hasRegistered || hasScrim,
          pathname: loc.current.pathname,
          canGoBack: loc.current.key !== 'default',
          lastExitPromptAt: lastPrompt.current,
          now: Date.now(),
        });
        switch (action.kind) {
          case 'close-layer':
            if (!closeTopLayer()) closeUnregisteredMenu();
            break;
          case 'history-back':
            navigate(-1);
            break;
          case 'navigate':
            navigate(action.to, { replace: true });
            break;
          case 'arm-exit':
            lastPrompt.current = Date.now();
            useToast.getState().show('Нажмите ещё раз, чтобы выйти', 2000);
            break;
          case 'exit':
            void App.exitApp();
            break;
        }
      });
      if (cancelled) void handle.remove();
      else remove = () => void handle.remove();
    });
    return () => {
      cancelled = true;
      remove?.();
    };
  }, [navigate]);

  return null;
}
